/**
 * The facets of the paged views (Everyone, Active members, a district): what the members list can be
 * narrowed by on the server, as URL parameters named like the API's own (`?tier=…&county=…`). Each is
 * one value; the facet counts (`getFacets`) say how many people each value would leave.
 */
import { DISTRICT_ADDRESSES, type DistrictAddress, type EventGroup, type MemberStatus, type MembersQuery, type RiskBandFilter } from "../../../api/dashboard.types";
import { getFacets } from "../../../api/insights";
import type { SegmentFilter } from "../../../api/actions.types";
import type { Facets, FacetsQuery, FacetValue } from "../../../api/insights.types";
import { LICENSE_STATES, type LicenseState } from "../../../api/licensing.types";
import { LEFT_OUT, LICENCE_REMINDER_HELD, LICENCE_REMINDER_STEPS, type LicenceReminderHeld, type LicenceReminderStepKey } from "../../../api/campaigns.types";
import { STANDING_LABEL } from "../../kit";
import { STANDING_ORDER } from "./filters";
import type { ViewDef } from "./views";

export const FACET_KEYS = ["event", "eventGroup", "status", "tier", "district", "county", "engagementBand", "hasEmail", "reachable", "expiringWithin", "licenseState", "licenceReminder", "riskBand", "usHouse", "stateSenate", "stateHouse", "districtAddress"] as const;
export type FacetKey = (typeof FACET_KEYS)[number];
export type FacetPicks = Partial<Record<FacetKey, string>>;

export interface FacetGroup {
  key: FacetKey;
  label: string;
  /** Where the live counts are, or null for a group the API cannot count (lapse risk). */
  from: keyof Facets | null;
  /** A value's words. */
  words: (value: string) => string;
  /** Values to offer when there are no counts to read them from. */
  fixed?: readonly string[];
  /** The order a person reads the values in (most to least, or by how far they have gone); a value it lacks goes last, A to Z. */
  order?: readonly string[];
}

const sentence = (value: string) => value.charAt(0).toUpperCase() + value.slice(1);
const number = (value: string) => `District ${value.split("-").pop()}`;
/** A MAC district's number: the facets name its group ("District 3"), the members list takes the number. */
const districtNo = (value: string | undefined) => value?.match(/\d+$/)?.[0];

/** People → Licences' four counts (#635), as a pick: the same people each count opens. */
export const LICENSE_STATE_WORDS: Readonly<Record<LicenseState, string>> = {
  expiring90: "Expires within 90 days",
  expired: "Expired",
  late_renewal: "Late renewal",
  suspended_limited: "Suspended or limited",
};

export const isLicenseState = (value: string | null | undefined): value is LicenseState => !!value && (LICENSE_STATES as readonly string[]).includes(value);

/** Licence reminders' steps (#339), as a pick: the people due at each, the count Email shows. */
export const LICENCE_REMINDER_WORDS: Readonly<Record<LicenceReminderStepKey, string>> = {
  three_months: "3 months before",
  two_months: "2 months before",
  one_month: "1 month before",
  after_expiry: "1 month after",
};

/** Why someone due at a step gets no reminder (Email's "Left out" rows open these). */
export const LEFT_OUT_WORDS: Readonly<Record<LicenceReminderHeld, string>> = {
  merged: "Merged duplicate",
  deceased: "Deceased",
  added_in_keystone: "Added in Keystone",
  no_email: "No email",
  invalid_email: "Invalid email",
  opted_out: "Opted out",
  bounced: "Bounced",
  no_consent: "No consent",
  not_in_ac: "Not in ActiveCampaign",
  not_on_state_list: "Not on the state list",
  suspended_limited: "License suspended or limited",
  expiry_unconfirmed: "Lapse not confirmed by a later list",
};

/** Who an email reaches (#716): everyone it does, or those one reason leaves out. A campaign's counts open these. */
export const REACHABLE_VALUES = ["yes", ...LEFT_OUT] as const;
export const isReachable = (value: string | null | undefined): value is string => !!value && (REACHABLE_VALUES as readonly string[]).includes(value);
const reachableWords = (value: string): string => (value === "yes" ? "Reached" : `Left out: ${(LEFT_OUT_WORDS[value as LicenceReminderHeld] ?? value).toLowerCase()}`);

/** Every `licenceReminder` value the API takes: a step, or a step and a reason it leaves someone out ("two_months:opted_out"). */
export const LICENCE_REMINDER_VALUES: readonly string[] = [
  ...LICENCE_REMINDER_STEPS,
  ...LICENCE_REMINDER_STEPS.flatMap((step) => LICENCE_REMINDER_HELD.filter((r) => step === "after_expiry" || r !== "expiry_unconfirmed").map((r) => `${step}:${r}`)),
];

export const isLicenceReminder = (value: string | null | undefined): value is string => !!value && LICENCE_REMINDER_VALUES.includes(value);

/** "2 months before" for a step; "2 months before · opted out" for the people a reason leaves out of it. */
export function licenceReminderWords(value: string): string {
  const [step, held] = value.split(":");
  const words = LICENCE_REMINDER_WORDS[step as LicenceReminderStepKey] ?? value;
  return held ? `${words} · ${(LEFT_OUT_WORDS[held as LicenceReminderHeld] ?? held).toLowerCase()}` : words;
}

/** The groups of an event's people the Filter offers beside "Registered for" (#705): Everyone is the event alone, and "registered last time" is the event's own page. */
export const EVENT_GROUP_WORDS: Readonly<Record<string, string>> = { members: "Members", non_members: "Non-members", vendors: "Vendors" };

export const FACET_GROUPS: readonly FacetGroup[] = [
  // "Registered for": the events come from the events list, not the counts (the sheet draws it, `EventFacet`).
  { key: "event", label: "Registered for", from: null, words: (v) => v },
  { key: "eventGroup", label: "Group", from: "eventGroup", order: Object.keys(EVENT_GROUP_WORDS), words: (v) => EVENT_GROUP_WORDS[v] ?? v },
  { key: "status", label: "Standing", from: "status", order: STANDING_ORDER, words: (v) => STANDING_LABEL[v as keyof typeof STANDING_LABEL] ?? sentence(v) },
  { key: "tier", label: "Tier", from: "tier", words: (v) => v },
  { key: "district", label: "District", from: "district", words: (v) => `District ${districtNo(v) ?? v}` },
  { key: "county", label: "County", from: "county", words: (v) => v },
  { key: "engagementBand", label: "Engagement", from: "engagementBand", order: ["high", "medium", "low", "unscored"], words: sentence },
  { key: "hasEmail", label: "Email", from: "hasEmail", words: (v) => (v === "true" ? "Has email" : "No email") },
  { key: "reachable", label: "Email reaches", from: null, words: reachableWords, fixed: REACHABLE_VALUES },
  { key: "expiringWithin", label: "Licence expires", from: "licenseExpiry", words: (v) => `Within ${v} days` },
  { key: "licenseState", label: "Licence", from: "licenseState", words: (v) => LICENSE_STATE_WORDS[v as LicenseState] ?? v, order: LICENSE_STATES },
  { key: "licenceReminder", label: "Licence reminder", from: "licenceReminder", words: licenceReminderWords, order: LICENCE_REMINDER_STEPS },
  { key: "riskBand", label: "Lapse risk", from: null, words: sentence, fixed: ["high", "medium", "low"] },
  { key: "usHouse", label: "US House", from: "usHouseDistrict", words: number },
  { key: "stateSenate", label: "State Senate", from: "stateSenateDistrict", words: number },
  { key: "stateHouse", label: "State House", from: "stateHouseDistrict", words: number },
];

/** Which address the three legislative filters read (Tim: home matters most, it is where they vote). A choice of how to read them, not a filter: it never counts as one. */
export const DISTRICT_ADDRESS_WORDS: Readonly<Record<DistrictAddress, string>> = { home: "Home", office: "Office", either: "Either", on_file: "CE21 main", state_list: "State list" };
/** Home (Tim: where they vote). The Home and Office districts were loaded and matched on production on 30 Oct 2026. */
export const DEFAULT_DISTRICT_ADDRESS: DistrictAddress = "home";
const LEGISLATIVE = ["usHouse", "stateSenate", "stateHouse"] as const;
const isDistrictAddress = (value: string | null | undefined): value is DistrictAddress => !!value && (DISTRICT_ADDRESSES as readonly string[]).includes(value);
/** The address the picks' districts are of: Home unless the address says otherwise. */
export const districtAddressOf = (picks: FacetPicks): DistrictAddress => (isDistrictAddress(picks.districtAddress) ? picks.districtAddress : DEFAULT_DISTRICT_ADDRESS);

/** What a view already fixes (Active members is one status, a district is one district), so it is not a choice. */
export function offered(view: ViewDef): FacetGroup[] {
  return FACET_GROUPS.filter((g) => !(g.key === "status" && view.status) && !(g.key === "district" && view.district));
}

/** The picks in an address. Only a paged view has them, and never one its view fixes; an event's own page has its groups instead. A group of an event means nothing without the event. */
export function readFacets(params: URLSearchParams, view: ViewDef): FacetPicks {
  // Licences is held whole, but its four counts filter it on the server (#635).
  if (view.id === "licences") {
    const state = params.get("licenseState");
    return isLicenseState(state) ? { licenseState: state } : {};
  }
  if (!view.paged || view.event) return {};
  const picks: FacetPicks = {};
  for (const g of offered(view)) {
    const value = params.get(g.key);
    if (value) picks[g.key] = value;
  }
  // The address the districts are of rides along only when it is not the default, Home.
  const address = params.get("districtAddress");
  if (isDistrictAddress(address) && address !== DEFAULT_DISTRICT_ADDRESS) picks.districtAddress = address;
  if (!picks.event || (picks.eventGroup && !(picks.eventGroup in EVENT_GROUP_WORDS))) delete picks.eventGroup;
  return picks;
}

/** Choose a value, or choose it again (or nothing) to clear it. */
export function setFacet(params: URLSearchParams, key: FacetKey, value: string): void {
  // The address is chosen, never toggled off: Home is said by its absence. A district picked stays picked; its counts re-read.
  if (key === "districtAddress") {
    if (isDistrictAddress(value) && value !== DEFAULT_DISTRICT_ADDRESS) params.set(key, value);
    else params.delete(key);
    params.delete("page");
    return;
  }
  if (!value || params.get(key) === value) params.delete(key);
  else params.set(key, value);
  // Another event (or none) leaves the group it was picked for.
  if (key === "event") params.delete("eventGroup");
  params.delete("page");
}

/** The facets in use: an event and the group of it picked are one, and the address the districts are of is not a filter. */
export const facetCount = (picks: FacetPicks): number => Object.keys(picks).filter((k) => k !== "eventGroup" && k !== "districtAddress").length;

export const facetKey = (picks: FacetPicks): string => FACET_KEYS.map((k) => picks[k] ?? "").join("|");

/** The picks as `GET /api/members` takes them. */
export function toQuery(picks: FacetPicks): MembersQuery {
  return {
    status: picks.status as MemberStatus | undefined,
    tier: picks.tier,
    district: districtNo(picks.district) ? (Number(districtNo(picks.district)) as MembersQuery["district"]) : undefined,
    county: picks.county,
    engagementBand: picks.engagementBand as MembersQuery["engagementBand"],
    hasEmail: picks.hasEmail === undefined ? undefined : picks.hasEmail === "true",
    expiringWithin: picks.expiringWithin ? (Number(picks.expiringWithin) as MembersQuery["expiringWithin"]) : undefined,
    licenseState: isLicenseState(picks.licenseState) ? picks.licenseState : undefined,
    licenceReminder: isLicenceReminder(picks.licenceReminder) ? picks.licenceReminder : undefined,
    reachable: isReachable(picks.reachable) ? picks.reachable : undefined,
    riskBand: picks.riskBand as RiskBandFilter | undefined,
    usHouse: picks.usHouse,
    stateSenate: picks.stateSenate,
    stateHouse: picks.stateHouse,
    // Sent whenever a district is picked, Home included: the API's own default is the CE21 address on file.
    districtAddress: LEGISLATIVE.some((k) => picks[k]) ? districtAddressOf(picks) : undefined,
    event: picks.event,
    eventGroup: picks.eventGroup as EventGroup | undefined,
  };
}

/** The same picks as a segment's filter (Copy emails and Export CSV over the whole list). */
export function toFilter(picks: FacetPicks): SegmentFilter {
  const q = toQuery(picks);
  return {
    ...(q.status && { status: q.status }),
    ...(q.tier && { tier: q.tier }),
    ...(q.district && { district: q.district }),
    ...(q.county && { county: q.county }),
    ...(q.engagementBand && { engagement_band: q.engagementBand }),
    ...(q.hasEmail !== undefined && { has_email: q.hasEmail }),
    ...(q.expiringWithin && { expiring_within: q.expiringWithin }),
    ...(q.licenseState && { license_state: q.licenseState }),
    ...(q.licenceReminder && { licence_reminder: q.licenceReminder }),
    ...(q.reachable && { reachable: q.reachable }),
    ...(q.riskBand && { risk_band: q.riskBand }),
    ...(q.usHouse && { us_house: q.usHouse }),
    ...(q.stateSenate && { state_senate: q.stateSenate }),
    ...(q.stateHouse && { state_house: q.stateHouse }),
    ...(q.districtAddress && { district_address: q.districtAddress }),
    ...(q.event && { event: q.event }),
    ...(q.eventGroup && { event_group: q.eventGroup }),
  };
}

/**
 * The picks, and what the view fixes (Active members' standing, a district), as `GET /api/insights/facets`
 * takes them: the counts then say what each value would leave of the list on screen.
 */
export function toFacetsQuery(picks: FacetPicks, view: ViewDef): FacetsQuery {
  const q = toQuery(picks);
  return {
    status: view.status ?? q.status,
    tier: q.tier,
    district: view.district ? `District ${view.district}` : picks.district,
    county: q.county,
    engagementBand: q.engagementBand,
    hasEmail: q.hasEmail,
    expiringWithin: q.expiringWithin,
    licenseState: q.licenseState,
    licenceReminder: q.licenceReminder,
    reachable: q.reachable,
    riskBand: q.riskBand,
    usHouseDistrict: q.usHouse,
    stateSenateDistrict: q.stateSenate,
    stateHouseDistrict: q.stateHouse,
    // Always: the district options are the districts that exist for the address chosen.
    districtAddress: districtAddressOf(picks),
    event: q.event,
    eventGroup: q.eventGroup,
  };
}

/** The counts for a view and its picks: the Filter sheet's query, and what People fetches ahead so the sheet opens full. */
export const facetsQuery = (view: ViewDef, picks: FacetPicks) => [`people:facets:${view.id}:${facetKey(picks)}`, () => getFacets(toFacetsQuery(picks, view))] as const;

/**
 * A group's values with their counts. Licence expiry is counted in disjoint bands (`30`, `60`, `90`), but
 * its filter is "within N days", so each option's count is the running total, which is what it leaves.
 */
export function options(group: FacetGroup, facets: Facets | undefined): Array<{ value: string; label: string; count: number | null }> {
  if (!group.from) return (group.fixed ?? []).map((value) => ({ value, label: group.words(value), count: null }));
  const rows = facets?.[group.from] ?? [];
  if (group.key === "expiringWithin") {
    let running = 0;
    return ["30", "60", "90"].map((value) => ({ value, label: group.words(value), count: (running += rows.find((r) => r.value === value)?.count ?? 0) }));
  }
  const skip = group.key === "status" ? new Set(["deceased", "unknown"]) : new Set<string>();
  // A district the list cannot filter by (a group with no number) is not offered: its click would show everyone.
  const usable = (value: string) => !skip.has(value) && (group.key !== "district" || districtNo(value) !== undefined);
  // A legislative district names who sits for it now, when VoterVoice knows (#341), and its state when it is not the home state's (#703).
  const words = (r: FacetValue) => `${r.state ? `${r.state} ` : ""}${group.words(r.value)}${r.official ? ` · ${r.official}` : ""}`;
  const values = rows.filter((r) => usable(r.value)).map((r) => ({ value: r.value, label: words(r), count: r.count }));
  const order = group.order;
  // A value the order does not name keeps the API's place, after the ones it does.
  const place = (value: string) => (order?.includes(value) ? order.indexOf(value) : (order?.length ?? 0));
  return order ? values.sort((a, b) => place(a.value) - place(b.value)) : values;
}
